import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Box, Typography, Card, CardContent, Button, Alert, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { redirectToTenant } from '../utils/tenantResolver';

const LicensePaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isMainDomain } = useTenant();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [additionalLicenses, setAdditionalLicenses] = useState<number | null>(null);
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    console.log('LicensePaymentSuccess: Component mounted. sessionId:', sessionId);
    console.log('LicensePaymentSuccess: All search params:', Object.fromEntries(searchParams.entries()));
    
    if (sessionId) {
      // Call backend to verify and process license payment
      verifyLicensePayment(sessionId);
    } else {
      console.error('LicensePaymentSuccess: No session_id found in URL');
      setError('Missing session ID. Please check your payment confirmation email or contact support.');
      setLoading(false);
    }
  }, [sessionId, searchParams]);

  const verifyLicensePayment = async (sessionId: string) => {
    try {
      console.log('LicensePaymentSuccess: Starting payment verification with session_id:', sessionId);
      setLoading(true);
      setError(null);
      setSuccess(false);
      
      // Use apiService to verify license payment
      // Note: This endpoint is public (no auth required) since Stripe redirects here
      console.log('LicensePaymentSuccess: Calling apiService.verifyLicensePayment...');
      const data = await apiService.verifyLicensePayment(sessionId);

      console.log('LicensePaymentSuccess: Backend response received:', JSON.stringify(data, null, 2));

      if (data.error) {
        const errorMsg = data.error || 'Failed to verify license payment';
        console.error('LicensePaymentSuccess: Backend returned error:', errorMsg);
        setError(errorMsg);
        setLoading(false);
        return;
      }

      if (data.message) {
        console.log('LicensePaymentSuccess: Payment verified successfully!');
        setSuccess(true);
        if (data.additionalLicenses) {
          setAdditionalLicenses(parseInt(data.additionalLicenses, 10));
          console.log('LicensePaymentSuccess: Additional licenses purchased:', data.additionalLicenses);
        }
        // So Licenses tab can show the new seat count immediately (no stale mount race).
        try {
          sessionStorage.setItem(
            'manageAccount:licensePurchaseApplied',
            JSON.stringify({
              newCount: data.newCount != null ? Number(data.newCount) : null,
              additionalLicenses:
                data.additionalLicenses != null ? Number(data.additionalLicenses) : null,
              at: Date.now(),
            })
          );
        } catch {
          /* ignore */
        }
        setLoading(false);

        const goToLicenses = () => {
          const token = localStorage.getItem('token');
          const user = localStorage.getItem('user');
          console.log('LicensePaymentSuccess: Before navigation - token exists:', !!token, 'user exists:', !!user);
          if (token && user) {
            console.log('LicensePaymentSuccess: User is authenticated, navigating to licenses page');
            navigate('/manage-account?tab=licenses&refresh=true', { replace: true });
          } else {
            console.warn('LicensePaymentSuccess: Token or user not found in localStorage. Redirecting to login.');
            const returnUrl = encodeURIComponent('/manage-account?tab=licenses&refresh=true');
            navigate(`/login?returnUrl=${returnUrl}`, { replace: true });
          }
        };

        // Brief success flash, then land on Licenses with updated count right away.
        setTimeout(goToLicenses, 400);
      } else {
        const errorMsg = 'Invalid response from server. Please contact support.';
        console.error('LicensePaymentSuccess: Backend response missing message:', data);
        setError(errorMsg);
        setLoading(false);
      }
    } catch (err: any) {
      console.error('LicensePaymentSuccess: Exception during payment verification:', err);
      console.error('LicensePaymentSuccess: Full error object:', err);
      console.error('LicensePaymentSuccess: Error response:', err.response);
      
      let errorMsg = 'An error occurred while verifying payment. Please contact support.';
      
      if (err.response) {
        // Server responded with error
        errorMsg = err.response.data?.error || err.response.data?.message || `Server error: ${err.response.status}`;
        console.error('LicensePaymentSuccess: Server error status:', err.response.status);
        console.error('LicensePaymentSuccess: Server error data:', err.response.data);
      } else if (err.request) {
        // Request was made but no response received
        errorMsg = 'No response from server. Please check your internet connection and try again.';
        console.error('LicensePaymentSuccess: No response received from server');
      } else {
        // Error setting up request
        errorMsg = err.message || 'Failed to verify payment. Please try again.';
        console.error('LicensePaymentSuccess: Request setup error:', err.message);
      }
      
      setError(errorMsg);
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ py: 8, textAlign: 'center' }}>
        {loading && !error && !success && (
          <Card>
            <CardContent>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Verifying payment...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait while we confirm your payment
              </Typography>
              {sessionId && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Session ID: {sessionId.substring(0, 20)}...
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {success && (
          <Card>
            <CardContent>
              <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom>
                Payment Successful!
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {additionalLicenses 
                  ? `You have successfully purchased ${additionalLicenses} additional license${additionalLicenses > 1 ? 's' : ''}.`
                  : 'Your license purchase has been completed successfully.'
                }
              </Typography>
              <Alert severity="info" sx={{ mt: 2, mb: 2, textAlign: 'left' }}>
                <Typography variant="body2">
                  <strong>One-time Payment:</strong> This was a one-time purchase. For subscription-based licenses, 
                  autopay will be enabled and your subscription will automatically renew at the end of each billing period.
                </Typography>
              </Alert>
              <Typography variant="body2" color="text.secondary" paragraph>
                Redirecting to your Licenses page...
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  navigate('/manage-account?tab=licenses&refresh=true', { replace: true });
                }}
                sx={{ mt: 2 }}
              >
                Go to Licenses
              </Button>
            </CardContent>
          </Card>
        )}

        {error && (
          <Card>
            <CardContent>
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
              <Button
                variant="contained"
                onClick={() => {
                  navigate('/manage-account?tab=licenses', { replace: true });
                }}
                sx={{ mr: 2 }}
              >
                Back to Licenses
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  navigate('/dashboard', { replace: true });
                }}
              >
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>
    </Container>
  );
};

export default LicensePaymentSuccess;

